import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { FormatDateShort } from "utils/dateUtils";
import { useMemo, useRef, useState } from "react";
import { useAppDispatch } from "hooks/hooks";
import InfoTooltip from "components/Popup/InfoTooltip";
import {
  getOutputForPrint,
  getOutputReportForPrint,
  requestDeletingOutput,
  selectState,
  updateConcreteBaseOutput,
  updateOutputTemporarily
} from "../outputSlice";
import { IOutputListItem } from "Dashboard/scenes/ConcreteBase/domain/IOutputListItem";
import PrintBillOfLanding from "./PrintBillOfLanding";
import PrintRequisition from "./PrintRequisition";
import PrintReport from "./PrintReport";
import { IGetOutputReportRequestParams } from "Dashboard/scenes/ConcreteBase/domain/IGetOutputReportRequestParams";
import { IOutputReportForPrint } from "Dashboard/scenes/ConcreteBase/domain/IOutputReportForPrint";
import { IConcreteBaseOutputUpdate } from "Dashboard/scenes/ConcreteBase/domain/IConcreteBaseOutputUpdate";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { CONCRETE_BASE_MODULE_NAME } from "Dashboard/scenes/ConcreteBase/domain/ConcreteBaseModule";
import { usePagePrint } from "hooks/usePagePrint";
import { FormatNumber } from "utils/numberUtils";
import { grey } from "@mui/material/colors";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutputModal from "./DeleteOutputModal";

interface IProps {
  lastTableElementRef?: any;
  handleScroll: (e: React.UIEvent<HTMLDivElement>) => void,
}

const TopSection = ({ lastTableElementRef, handleScroll }: IProps) => {
  const { outputs } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const componentRefRequisition = useRef<HTMLDivElement>(null);
  const componentRefBillOfLanding = useRef<HTMLDivElement>(null);
  const componentRefReport = useRef<HTMLDivElement>(null);
  const [outputIdToPrintRequisition, setOutputIdToPrintRequisition]
    = useState<number | null>(null);
  const [outputIdToPrintBillOfLanding, setOutputIdToPrintBillOfLanding]
    = useState<number | null>(null);
  const [requestParamsToPrintReport, setRequestParamsToPrintReport]
    = useState<IGetOutputReportRequestParams | null>(null);

  const dispatch = useAppDispatch();

  const handlePrintRequisition = usePagePrint(
    componentRefRequisition, { landscape: true, margins: "0" });

  const handlePrintBillOfLanding = usePagePrint(
    componentRefBillOfLanding, { landscape: true, margins: "0" });

  const handlePrintReport = usePagePrint(
    componentRefReport, { margins: "0" });

  const printRequisition = (outputListItemToPrint: IOutputListItem) => {
    setOutputIdToPrintRequisition(outputListItemToPrint.outputId);
    dispatch(getOutputForPrint(outputListItemToPrint.outputId));
  };

  const doPrintRequisition = (outputListItemToPrint: IOutputListItem | undefined) => {
    if (outputIdToPrintRequisition === outputListItemToPrint?.outputId) {
      handlePrintRequisition();
      setOutputIdToPrintRequisition(null);
    }
  };

  const printBillOfLanding = (outputListItemToPrint: IOutputListItem) => {
    setOutputIdToPrintBillOfLanding(outputListItemToPrint.outputId);
    dispatch(getOutputForPrint(outputListItemToPrint.outputId));
  };

  const doPrintBillOfLanding = (outputListItemToPrint: IOutputListItem | undefined) => {
    if (outputIdToPrintBillOfLanding === outputListItemToPrint?.outputId) {
      handlePrintBillOfLanding();
      setOutputIdToPrintBillOfLanding(null);
    }
  }

  const printReport = (outputListItemToPrint: IOutputListItem) => {
    setRequestParamsToPrintReport({
      inputItemId: outputListItemToPrint.inputItemId,
      repositoryId: outputListItemToPrint.repository.repositoryId,
      date: outputListItemToPrint.dateOfOutput,
    });
    dispatch(getOutputReportForPrint({
      inputItemId: outputListItemToPrint.inputItemId,
      repositoryId: outputListItemToPrint.repository.repositoryId,
      date: outputListItemToPrint.dateOfOutput,
    }));
  };

  const doPrintReport = (outputReportForPrint: IOutputReportForPrint | undefined) => {
    if (requestParamsToPrintReport !== null
      && requestParamsToPrintReport?.date === outputReportForPrint?.date
      && requestParamsToPrintReport?.inputItemId === outputReportForPrint?.outputItem.itemId) {
      handlePrintReport();
      setRequestParamsToPrintReport(null);
    }
  }

  const onChangeBasicField = (data: IConcreteBaseOutputUpdate) => {
    updateBasicFieldWithDelay(data);
    dispatch(updateOutputTemporarily(data));
  };

  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IConcreteBaseOutputUpdate) => {
      try {
        await dispatch(updateConcreteBaseOutput(data)).unwrap();
        toast.success("Izmena je uspešno zabeležena.");
      } catch (status: any) {
        if (status === 403) {
          toast.error("Neočekivana greška");
        } else if (status === 400) {
          toast.error("Neočekivana greška");
        }
      }
    }, 1000),
    [dispatch]
  );

  return (
    <>
      <div className="g-grid g-header">
        <div className="row">
          <div className="col separator-line"></div>
          <div className="col lowercase center separator-line">
            šifra a.
          </div>
          <div className="col lowercase center separator-line">
            br. otpremnice
          </div>
          <div className="col lowercase separator-line table-header-padding">
            ime
          </div>
          <div className="col lowercase center separator-line">količina iz.</div>
          <div className="col lowercase center separator-line">mera</div>
          <div className="col lowercase center separator-line">datum izlaza</div>
          <div className="col lowercase center separator-line">
            odvezao i registracija
          </div>
          <div className="col lowercase center separator-line">gde je otišlo</div>
          <div className="col lowercase center separator-line">dobavljač</div>
          <div className="col lowercase separator-line table-header-padding">
            napomena
          </div>
        </div>
      </div>

      <div className="g-grid g-content" onScroll={handleScroll}>
        {outputs &&
          outputs.map((output, outputIndex) => {
            const isLast = outputIndex === outputs.length - 1;
            return (
              <div
                className="row" key={`${output.outputItemId}`} ref={isLast ? lastTableElementRef : undefined}
              >
                <div className="col">
                  {output.orderNumber === 1 ? (
                    <div className="bg-item bg-transparent center">
                      {output.shouldPrintBillOfLanding
                        ? (
                          <InfoTooltip tooltip="Štampaj otpremnicu" element="span"
                            onClick={() => printBillOfLanding(output)}>
                            <FontAwesomeIcon fontSize={13} icon={faPrint} />
                          </InfoTooltip>
                        ) : (<span className="no-print-space"></span>)}
                      {output.requisitionId ? (
                        <InfoTooltip
                          tooltip={`Štampaj trebovanje ${output.requisitionId}`}
                          element="span"
                          onClick={() => printRequisition(output)}>
                          <FontAwesomeIcon fontSize={13} icon={faPrint} />
                        </InfoTooltip>
                      ) : (<span className="no-print-space"></span>)}
                      {output.shouldPrintReport ? (
                        <InfoTooltip tooltip="Štampaj izveštaj" element="span"
                          onClick={() => printReport(output)}>
                          <FontAwesomeIcon fontSize={13} icon={faPrint} />
                        </InfoTooltip>
                      ) : (<span className="no-print-space"></span>)}
                    </div>)
                    : (
                      <div className="bg-item bg-transparent center"></div>
                    )}
                </div>
                <div className="col light center">
                  <InfoTooltip tooltip={`${output.itemCode}`}
                    classesProp="bg-item-border-light bg-transparent center">
                    {output.itemCode}
                  </InfoTooltip>
                </div>
                <div className="col center">
                  <InfoTooltip tooltip={`${output.billOfLandingId}`} element="span"
                    classesProp="bg-item spec-danger-span center">
                    {output.billOfLandingId}
                  </InfoTooltip>
                  <span className="light order-number">{output.orderNumber}</span>
                </div>

                <div className="col name">
                  <div className="bg-item overflowMinWidth">
                    <InfoTooltip
                      tooltip={output.name}
                      classesProp={`bg-text-overflow ${output.isOwnProduct ? "strong" : ""}`}
                    >
                      {output.name}
                    </InfoTooltip>
                  </div>
                  <InfoTooltip
                    tooltip={output.repository.name}
                    classesProp="bg-item repository"
                    element="label"
                  >
                    {output.repository.shortName}
                  </InfoTooltip>
                </div>
                <div className="col">
                  <div className="bg-item right danger">
                    <span>
                      {FormatNumber(output.quantity)}
                    </span>
                  </div>
                </div>
                <div className="col light">
                  <div className="bg-item bg-transparent">
                    <span>{output.unit.name}</span>
                  </div>
                </div>
                <div className="col">
                  <div className="bg-item center">
                    <span>
                      {FormatDateShort(output.dateOfOutput)}
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="bg-item center">
                    <span>{output?.takenAwayByDriverAndVehicle ?? ""}</span>
                  </div>
                </div>
                <div className="col">
                  {output.orderNumber === 1
                    ? <div className={`bg-item center 
                            ${output.whereWentToForReal !== output.whereWentToOnBill
                        ? " projectOverrideFirst" : ""}`}>
                      <InfoTooltip tooltip={output.whereWentToForReal} element="span">
                        {output.whereWentToOnBill}
                      </InfoTooltip>
                    </div>
                    : <div className={`bg-item bg-transparent right
                        destinationRemainingItems
                        ${output.whereWentToForReal !== output.whereWentToOnBill
                        ? " projectOverrideFirst" : ""}`}>
                      <span>
                        {output.whereWentToOnBill}
                      </span>
                    </div>}
                </div>
                <div className="col">
                  {output.orderNumber === 1
                    ? <div className="bg-item center">
                      <InfoTooltip tooltip={output.supplier} element="span">
                        {output.supplier}
                      </InfoTooltip>
                    </div>
                    : <></>}
                </div>
                <div className="col">
                  <input className="bg-item bg-transparent light" type="text"
                    value={output.note}
                    onChange={(e) => {
                      onChangeBasicField({ outputId: output.outputId, note: e.target.value })
                    }}
                    disabled={!hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)}
                  />
                </div>
                <div className="col">
                  {output.isOwnProduct && hasFullAccessTo(user, CONCRETE_BASE_MODULE_NAME)
                    ? (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(requestDeletingOutput(output));
                        }}
                      >
                        <CancelIcon
                          style={{ cursor: "pointer", fontSize: 15, color: grey[300] }}
                        />
                      </span>
                    )
                    : (<></>)}
                </div>
                <div className="col">
                  <div className="bg-item bg-transparent left">
                    <span className="light">trebovanje: {output.requisitionId ?? '-'}; radni nalog: {output.produceWorkOrderId ?? '-'}</span>
                  </div>
                </div>
              </div>
            );
            // });
          })}
      </div>

      <div ref={componentRefRequisition} className="print-area">
        <PrintRequisition doPrint={doPrintRequisition} />
      </div>
      <div ref={componentRefBillOfLanding} className="print-area">
        <PrintBillOfLanding doPrint={doPrintBillOfLanding} />
      </div>
      <div ref={componentRefReport} className="print-area">
        <PrintReport doPrint={doPrintReport} />
      </div>
      <DeleteOutputModal />
    </>
  );
};

export default TopSection;
